import type { CSSProperties } from "react";

/**
 * Il posto che il contenuto occuperà, mentre arriva.
 *
 * ── Perché non «Carico…» ──────────────────────────────────────────────────
 *
 * Perché una riga di testo alta venti pixel viene sostituita da una lista alta
 * quattrocento, e **la pagina salta**. Chi stava già leggendo qualcosa se lo
 * ritrova spostato sotto il dito.
 *
 * Uno scheletro della forma giusta non fa saltare niente. E il movimento non è
 * decorazione: distingue «sto aspettando» da «è vuoto», che con un rettangolo
 * grigio fermo sarebbero indistinguibili.
 */
export interface SkeletonProps {
  width?: number | string;
  height?: number;
  radius?: number;
  style?: CSSProperties;
}

export function Skeleton({ width = "100%", height = 14, radius = 7, style }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className="qota-skeleton"
      style={{
        display: "block",
        width,
        height,
        borderRadius: radius,
        background: "var(--skeleton-base)",
        ...style,
      }}
    />
  );
}

/**
 * Lo scheletro di una riga con icona, due testi e un importo.
 *
 * È la forma delle spese, dei saldi e delle attività: una sola qui invece di
 * tre quasi uguali sparse, così quando la riga vera cambia proporzioni c'è un
 * posto solo da correggere.
 */
export function SkeletonRow({ lines = 2 }: { lines?: 1 | 2 }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 56,
        padding: "8px 4px",
      }}
    >
      <Skeleton width={36} height={36} radius={12} />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <Skeleton width="55%" height={13} />
        {lines === 2 && <Skeleton width="32%" height={11} />}
      </div>

      <Skeleton width={58} height={15} />
    </div>
  );
}

/**
 * Un elenco di righe in attesa.
 *
 * Le larghezze variano di riga in riga: tutte identiche si leggono come una
 * griglia, cioè come un elemento di interfaccia, invece che come contenuto che
 * sta arrivando.
 */
export function SkeletonList({ rows = 4, lines = 2 }: { rows?: number; lines?: 1 | 2 }) {
  return (
    <div
      // Uno stato di attesa va annunciato, altrimenti chi usa uno screen
      // reader trova una schermata che tace e poi cambia da sola.
      role="status"
      aria-busy="true"
      aria-label="Caricamento in corso"
    >
      {Array.from({ length: rows }, (_, index) => (
        <SkeletonRow key={index} lines={lines} />
      ))}
    </div>
  );
}

/**
 * Lo scheletro di una card di gruppo.
 *
 * Non è una `SkeletonRow` più alta: la card ha un riquadro colorato, due righe
 * di testo di larghezza diversa, una pastiglia a destra e una riga separata in
 * basso per l'ultimo movimento. Uno scheletro che sbaglia la forma **provoca**
 * il salto che dovrebbe evitare — che è tutto il motivo per cui esiste.
 */
export function SkeletonCard() {
  return (
    <div
      style={{
        background: "var(--surface-card)",
        borderRadius: "var(--radius-card)",
        padding: "14px 16px",
        border: "1px solid var(--border-default)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Skeleton width={38} height={38} radius={13} />

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 7 }}>
          <Skeleton width="48%" height={14} />
          <Skeleton width="30%" height={11} />
        </div>

        <Skeleton width={72} height={24} radius={999} />
      </div>

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
        <Skeleton width="62%" height={11} />
      </div>
    </div>
  );
}

/** Un elenco di card in attesa. */
export function SkeletonCards({ count = 3 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Caricamento in corso"
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
    >
      {Array.from({ length: count }, (_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>
  );
}

/**
 * Un rettangolo grande: un grafico, un codice QR, una scheda che sta arrivando.
 *
 * Per le forme che non sono elenchi e non vale la pena di imitare nel
 * dettaglio: quello che conta è che occupino **lo spazio giusto**, così quando
 * il contenuto arriva niente si sposta.
 */
export function SkeletonBlock({
  height = 160,
  label = "Caricamento in corso",
}: {
  height?: number;
  label?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      <Skeleton height={height} radius={16} />
    </div>
  );
}
